/**
 * Queries/mutations de técnicos e ordens de serviço (spec 016). Chaves
 * `['erp', domínio, visão, params]`; as mutations invalidam por prefixo
 * (`['erp','service-orders']` / `['erp','technicians']`).
 */
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  SERVICE_ORDER_ERROR_LABELS,
  type ServiceOrder,
  type ServiceOrderDraft,
  type Technician,
  type TechnicianAbsence,
  type TechnicianAvailability,
  type TechnicianSyncStatus,
} from '@erp/shared';
import { api, type ApiError } from '@/lib/api-client';
import { errorMessage as genericErrorMessage } from '@/features/tickets/ticket-format';

export const TECHNICIANS_KEY = ['erp', 'technicians'] as const;
export const SERVICE_ORDERS_KEY = ['erp', 'service-orders'] as const;

export function techniciansQuery() {
  return queryOptions({
    queryKey: [...TECHNICIANS_KEY, 'list'] as const,
    queryFn: async ({ signal }) => {
      const res = await api<Technician[]>('/technicians', { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

/**
 * Busca do seletor de técnico do ticket: o servidor filtra (nome, cidade,
 * região, sem acento) e devolve poucos — a lista sincronizada tem ~1.800.
 * Sem `includeInactive`, só os ativos.
 */
export const TECHNICIAN_PICKER_LIMIT = 30;

export function technicianSearchQuery(search: string, includeInactive: boolean) {
  return queryOptions({
    queryKey: [...TECHNICIANS_KEY, 'search', { search, includeInactive }] as const,
    queryFn: async ({ signal }) => {
      const res = await api<Technician[]>('/technicians', {
        signal,
        query: {
          search: search.trim() || undefined,
          limit: TECHNICIAN_PICKER_LIMIT,
          active: includeInactive ? undefined : 'true',
        },
      });
      if (res.error) throw res.error;
      return res.data;
    },
    staleTime: 60_000,
  });
}

/**
 * Status da sincronização com a origem (admin). Enquanto a última rodada está
 * em voo, a tela pergunta de novo a cada 3s — a rodada roda em background e o
 * único jeito de ver o fim é perguntar.
 */
export function technicianSyncStatusQuery(enabled: boolean) {
  return queryOptions({
    queryKey: [...TECHNICIANS_KEY, 'sync'] as const,
    queryFn: async ({ signal }) => {
      const res = await api<TechnicianSyncStatus>('/technicians/sync', { signal });
      if (res.error) throw res.error;
      return res.data;
    },
    enabled,
    refetchInterval: (query) => {
      const data = query.state.data;
      return data?.available && data.lastRun?.status === 'running' ? 3_000 : false;
    },
  });
}

/** "Sincronizar agora" — 202 com a rodada; o fim chega pelo status. */
export function useStartTechnicianSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await api<{ runId: number }>('/technicians/sync', { method: 'POST' });
      if (res.error) throw res.error;
      return res.data;
    },
    // o prefixo inteiro: o status (que passa a perguntar de 3 em 3s) e a lista
    onSuccess: () => qc.invalidateQueries({ queryKey: TECHNICIANS_KEY }),
  });
}

export function technicianAbsencesQuery(technicianId: number) {
  return queryOptions({
    queryKey: [...TECHNICIANS_KEY, 'absences', { technicianId }] as const,
    queryFn: async ({ signal }) => {
      const res = await api<TechnicianAbsence[]>(`/technicians/${technicianId}/absences`, { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

/**
 * Disponibilidade na data (e cidade). staleTime curto: a carga do dia muda a
 * cada OS agendada, e a lista é o que decide quem recebe a próxima.
 */
export function technicianAvailabilityQuery(date: string, city: string) {
  return queryOptions({
    queryKey: [...TECHNICIANS_KEY, 'availability', { date, city }] as const,
    queryFn: async ({ signal }) => {
      const res = await api<TechnicianAvailability[]>('/technicians/availability', {
        signal,
        query: { date, city: city.trim() || undefined },
      });
      if (res.error) throw res.error;
      return res.data;
    },
    enabled: /^\d{4}-\d{2}-\d{2}$/.test(date),
    staleTime: 10_000,
  });
}

export function ticketServiceOrdersQuery(ticketNumber: number) {
  return queryOptions({
    queryKey: [...SERVICE_ORDERS_KEY, 'by-ticket', { ticketNumber }] as const,
    queryFn: async ({ signal }) => {
      const res = await api<ServiceOrder[]>(`/tickets/${ticketNumber}/service-orders`, { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

export function serviceOrderQuery(ticketNumber: number, id: number) {
  return queryOptions({
    queryKey: [...SERVICE_ORDERS_KEY, 'detail', { ticketNumber, id }] as const,
    queryFn: async ({ signal }) => {
      const res = await api<ServiceOrder>(`/tickets/${ticketNumber}/service-orders/${id}`, { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

/** Rascunho a partir do ticket — lido na abertura do diálogo, nunca em cache. */
export async function fetchServiceOrderDraft(ticketNumber: number): Promise<ServiceOrderDraft> {
  const res = await api<ServiceOrderDraft>(`/tickets/${ticketNumber}/service-orders/draft`);
  if (res.error) throw res.error;
  return res.data;
}

/** Rótulos da OS em camada sobre o fallback genérico (padrão ai-actions). */
export function serviceOrderErrorMessage(e: unknown): string {
  const err = e as Partial<ApiError> | null;
  if (err && typeof err.error === 'string') {
    const label = SERVICE_ORDER_ERROR_LABELS[err.error];
    if (label) return label;
  }
  return genericErrorMessage(e);
}

/** Data de hoje no fuso de São Paulo (o produto inteiro assume SP). */
export function todaySaoPaulo(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date());
}

export function formatIsoDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}
